import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa6';
import { NS } from '../U3204';
import { useU3204Context } from '../U3204Context';
import type { U3204DisplayMessage, U3204QuotaAxis, U3204QuotaStatus } from '../U3204Service';
import { u3204BuildMissionModelFooterDisplay } from '../utils/u3204BuildMissionModelFooterLabel';
import { u3204FormatTokenCount } from '../utils/u3204ComputeConversationTokenUsage';
import {
  u3204ResolveGroupQuotaBarColor,
  u3204ResolvePercentTextClass,
  u3204ResolveQuotaBarColor,
  u3204ResolveQuotaBarPercent,
} from '../utils/u3204QuotaUsageVisibility';
import { U3204MissionModelNotConfiguredTag, U3204MissionModelTag } from './U3204MissionModelTag';
import U3204TokenUsageSummary from './U3204TokenUsageSummary';

interface U3204QuotaUsageFooterProps {
  quotaStatus: U3204QuotaStatus;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

interface U3204QuotaAxisRowProps {
  axis: 'user' | 'group';
  data: U3204QuotaAxis;
  providerName: string;
}

interface U3204QuotaMetaRowProps {
  providerName: string;
  messages: U3204DisplayMessage[];
  trailing?: React.ReactNode;
}

function U3204QuotaMissionTags({ providerName }: { providerName: string }) {
  const { t } = useTranslation(NS);
  const { activeMission } = useU3204Context();

  const missionDisplay = useMemo(() => {
    if (!activeMission) return null;
    return u3204BuildMissionModelFooterDisplay({
      defaultProvider: activeMission.defaultProvider,
      defaultModel: activeMission.defaultModel,
      notConfiguredLabel: t('missionModelNotConfigured', { ns: NS }),
    });
  }, [activeMission, t]);

  if (missionDisplay?.kind === 'notConfigured') {
    return <U3204MissionModelNotConfiguredTag label={missionDisplay.label} />;
  }

  if (missionDisplay?.kind === 'tags') {
    return (
      <>
        {missionDisplay.tags.map((tag) => (
          <U3204MissionModelTag key={tag.kind} tag={tag} />
        ))}
      </>
    );
  }

  return (
    <U3204MissionModelTag
      tag={{
        kind: 'provider',
        label: providerName,
        title: providerName,
      }}
    />
  );
}

function U3204QuotaMetaRow({ providerName, messages, trailing }: U3204QuotaMetaRowProps) {
  return (
    <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
        <U3204QuotaMissionTags providerName={providerName} />
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
        <U3204TokenUsageSummary messages={messages} />
        {trailing}
      </div>
    </div>
  );
}

function U3204QuotaAxisRow({ axis, data, providerName }: U3204QuotaAxisRowProps) {
  const { t, i18n } = useTranslation(NS);

  if (data.quotaMonthlyTokens == null) {
    return null;
  }

  const percent = u3204ResolveQuotaBarPercent(data);
  const roundedPercent = Math.round(percent * 10) / 10;
  const barColor = axis === 'user' ? u3204ResolveQuotaBarColor(percent) : u3204ResolveGroupQuotaBarColor(percent);
  const label = axis === 'user' ? t('quota.userLabel', { ns: NS }) : t('quota.groupLabel', { ns: NS });
  const warningKey = axis === 'user' ? 'quota.userWarning' : 'quota.groupWarning';
  const ariaLabel = `${providerName} — ${label} — ${t('quota.percent', { ns: NS, percent: roundedPercent })}`;

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 text-xs">
        <span className="font-medium text-foreground">{label}</span>
        <span className="text-muted-foreground">
          {t('quota.percent', { ns: NS, percent: roundedPercent })}{' '}
          {t('quota.detail', {
            ns: NS,
            usage: u3204FormatTokenCount(data.usageTokens, i18n.language),
            quota: u3204FormatTokenCount(data.quotaMonthlyTokens, i18n.language),
          })}
        </span>
      </div>
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(percent)}
        aria-label={ariaLabel}
        title={percent >= 80 ? t(warningKey, { ns: NS, percent: roundedPercent }) : undefined}
      >
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

const U3204QuotaUsageFooter: React.FC<U3204QuotaUsageFooterProps> = ({
  quotaStatus,
  expanded: expandedProp,
  onExpandedChange,
}) => {
  const { t } = useTranslation(NS);
  const { messages } = useU3204Context();
  const [expandedInternal, setExpandedInternal] = useState(false);
  const expanded = expandedProp ?? expandedInternal;
  const setExpanded = onExpandedChange ?? setExpandedInternal;

  const providerName = quotaStatus.provider.name || quotaStatus.provider.code;
  const userPercent = u3204ResolveQuotaBarPercent(quotaStatus.user);
  const roundedUserPercent = Math.round(userPercent * 10) / 10;
  const showUserPercent = quotaStatus.user.quotaMonthlyTokens != null;

  const userPercentTrailing = showUserPercent ? (
    <span
      className={`text-xs tabular-nums ${u3204ResolvePercentTextClass(userPercent)}`}
      title={t('quota.userWarning', { ns: NS, percent: roundedUserPercent })}
    >
      {t('quota.percent', { ns: NS, percent: roundedUserPercent })}
    </span>
  ) : null;

  const toggleExpanded = () => setExpanded(!expanded);

  return (
    <div
      className={`min-w-0 flex-1 ${expanded ? 'border-t border-border/50 pt-2' : ''}`}
      aria-label={t('quota.footerAria', { ns: NS })}
    >
      {expanded ? (
        <div className="space-y-2">
          <button
            type="button"
            onClick={toggleExpanded}
            className="flex w-full items-center justify-between gap-2 text-left"
            aria-expanded={true}
            title={t('quota.collapse', { ns: NS })}
          >
            <span className="text-xs font-medium text-foreground">{t('quota.monthlyLabel', { ns: NS })}</span>
            <FaChevronUp className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
          </button>
          <U3204QuotaAxisRow axis="user" data={quotaStatus.user} providerName={providerName} />
          <U3204QuotaAxisRow axis="group" data={quotaStatus.group} providerName={providerName} />
          <U3204QuotaMetaRow providerName={providerName} messages={messages} />
        </div>
      ) : (
        <button
          type="button"
          onClick={toggleExpanded}
          className="flex w-full min-w-0 items-center gap-2 text-left"
          aria-expanded={false}
          title={t('quota.expand', { ns: NS })}
        >
          <U3204QuotaMetaRow providerName={providerName} messages={messages} trailing={userPercentTrailing} />
          <FaChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default U3204QuotaUsageFooter;
